import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Avatar, Box, Button, Chip, CircularProgress, Container, Divider, Paper, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { getPublicData, serverPath } from '../../services/ajax';
import { APP_NAME, APP_ORGANISM, APP_SHORT_NAME } from '../../const/app';
import logo from '../../images/logo_blanco_color.png';

const ESTADOS = {
    VALIDA: {
        color: '#2e7d32', bg: '#e8f5e9', icon: CheckCircleIcon,
        titulo: 'Credencial válida', subtitulo: 'La credencial es auténtica y está vigente.',
    },
    VENCIDA: {
        color: '#ed6c02', bg: '#fff4e5', icon: WarningAmberIcon,
        titulo: 'Credencial vencida', subtitulo: 'Esta credencial expiró y ya no es válida.',
    },
    REVOCADA: {
        color: '#d32f2f', bg: '#fdecea', icon: BlockIcon,
        titulo: 'Credencial revocada', subtitulo: 'Esta credencial fue revocada y no es válida.',
    },
    SUSPENDIDA: {
        color: '#ed6c02', bg: '#fff4e5', icon: WarningAmberIcon,
        titulo: 'Credencial suspendida', subtitulo: 'Esta credencial está suspendida temporalmente.',
    },
    PERSONAL_INACTIVO: {
        color: '#d32f2f', bg: '#fdecea', icon: CancelIcon,
        titulo: 'Personal no activo', subtitulo: 'La persona no figura como personal activo del Ministerio.',
    },
    INVALIDA: {
        color: '#616161', bg: '#f5f5f5', icon: HelpOutlineIcon,
        titulo: 'Código inválido', subtitulo: 'El código o token no corresponde a una credencial válida.',
    },
    ERROR_CONEXION: {
        color: '#616161', bg: '#f5f5f5', icon: WifiOffIcon,
        titulo: 'Error de conexión', subtitulo: 'No se pudo verificar la credencial. Revisá tu conexión e intentá de nuevo.',
    },
};

function Page() {
    const { codigoPublico, token } = useParams();
    const [estado, setEstado] = useState('LOADING');
    const [datos, setDatos] = useState(null);

    const verificar = useCallback(async () => {
        setEstado('LOADING');
        const resp = await getPublicData(`public/credenciales/${codigoPublico}/${token}`);
        if (resp.error) {
            setEstado('ERROR_CONEXION');
            setDatos(null);
            return;
        }
        const d = resp.data || {};
        setDatos(d);
        setEstado(d.estado_credencial || 'INVALIDA');
    }, [codigoPublico, token]);

    useEffect(() => { verificar(); }, [verificar]);

    const conf = ESTADOS[estado] || ESTADOS.INVALIDA;
    const Icono = conf.icon;
    const esValida = estado === 'VALIDA';

    const fotoSrc = datos?.foto_url ? (serverPath() + datos.foto_url) : null;

    return (
        <Box sx={{
            minHeight: '100vh', width: '100%',
            background: 'linear-gradient(160deg, #00203a 0%, #013a5e 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            py: { xs: 2, sm: 4 }, px: 2,
        }}>
            <Box sx={{ mb: 2, textAlign: 'center' }}>
                <img src={logo} alt="Ministerio de Salud Chaco" style={{ height: 56, maxWidth: '80vw' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', display: 'block', mt: 0.5 }}>
                    {APP_ORGANISM}
                </Typography>
            </Box>

            <Container maxWidth="sm" disableGutters>
                <Paper elevation={8} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    {estado === 'LOADING' ? (
                        <Box sx={{ p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <CircularProgress />
                            <Typography color="text.secondary">Verificando credencial…</Typography>
                        </Box>
                    ) : (
                        <Box>
                            {/* Encabezado de estado */}
                            <Box sx={{ bgcolor: conf.bg, p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                                <Icono sx={{ fontSize: 64, color: conf.color }} />
                                <Typography variant="h5" sx={{ color: conf.color, fontWeight: 700, mt: 1 }}>
                                    {conf.titulo}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)', mt: 0.5 }}>
                                    {conf.subtitulo}
                                </Typography>
                            </Box>

                            {/* Datos mínimos solo si es válida */}
                            {esValida && (
                                <Box sx={{ p: { xs: 3, sm: 4 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Avatar src={fotoSrc || undefined} sx={{ width: 80, height: 80, bgcolor: '#00203a' }}>
                                            {!fotoSrc && (datos?.nombre_apellido ? datos.nombre_apellido.charAt(0) : '?')}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                                {datos?.nombre_apellido || '-'}
                                            </Typography>
                                            {datos?.texto_estado_personal && (
                                                <Chip
                                                    icon={<VerifiedUserIcon />}
                                                    label={datos.texto_estado_personal}
                                                    color="success"
                                                    size="small"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            )}
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 2 }} />

                                    <Detalle label="Área de desempeño" value={datos?.area_desempeno || 'No informada'} />
                                    <Detalle label="CUOF del área" value={datos?.cuof || 'No informado'} />
                                    <Detalle label="Función actual" value={datos?.funcion_actual || 'No informada'} />
                                </Box>
                            )}

                            {/* Pie: fecha de verificación y reintento */}
                            <Box sx={{ px: { xs: 3, sm: 4 }, pb: 3, pt: esValida ? 0 : 2 }}>
                                {datos?.verificado_at && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                                        Verificado el {new Date(datos.verificado_at).toLocaleString('es-AR')}
                                    </Typography>
                                )}
                                {(estado === 'ERROR_CONEXION') && (
                                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                                        <Button variant="contained" onClick={verificar}>Reintentar</Button>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </Paper>

                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', textAlign: 'center', mt: 2 }}>
                    Verificación pública de credenciales · {APP_SHORT_NAME}
                </Typography>
            </Container>
        </Box>
    );
}

function Detalle({ label, value }) {
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{value}</Typography>
        </Box>
    );
}

export default Page;
